import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

const links = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/portfolio", label: "Portfolio" },
    { to: "/contact", label: "Contact" },
];

export const Nav = () => {
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const location = useLocation();
    const isHome = location.pathname === "/";

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 60);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    // On the home page, hide the nav while user is on the hero. Reveal once scrolled past.
    const hideOnHero = isHome && !scrolled;

    return (
        <header
            data-testid="site-nav"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
                scrolled ? "backdrop-blur-md bg-[rgba(8,6,8,0.55)]" : ""
            } ${hideOnHero ? "opacity-0 pointer-events-none -translate-y-2" : "opacity-100 translate-y-0"}`}
        >
            <div className="flex items-center justify-between px-6 md:px-12 lg:px-16 py-6">
                <Link
                    to="/"
                    data-testid="brand-link"
                    className="font-serif-display text-xl md:text-2xl tracking-tighter text-aplum hover:text-aplum-accent transition-colors duration-500"
                >
                    A_Plum
                </Link>

                {/* Desktop nav top-right */}
                <nav className="hidden md:flex items-center gap-10">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `nav-link font-sans-display text-xs uppercase tracking-widest-x text-aplum-dim hover:text-aplum transition-colors duration-500 ${
                                    isActive ? "text-aplum active" : ""
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <button
                    data-testid="mobile-menu-toggle"
                    onClick={() => setMobileOpen((v) => !v)}
                    className="md:hidden text-aplum p-2"
                    aria-label="Toggle menu"
                >
                    <div className="flex flex-col gap-1.5">
                        <span className={`block h-px w-6 bg-current transition-transform duration-300 ${mobileOpen ? "translate-y-1.5 rotate-45" : ""}`} />
                        <span className={`block h-px w-6 bg-current transition-opacity duration-300 ${mobileOpen ? "opacity-0" : ""}`} />
                        <span className={`block h-px w-6 bg-current transition-transform duration-300 ${mobileOpen ? "-translate-y-1.5 -rotate-45" : ""}`} />
                    </div>
                </button>
            </div>

            {/* Mobile menu */}
            <div
                data-testid="mobile-menu"
                className={`md:hidden overflow-hidden transition-all duration-500 ${mobileOpen ? "max-h-96" : "max-h-0"}`}
            >
                <nav className="flex flex-col gap-6 px-6 pb-10 pt-2 bg-aplum-surface/95 backdrop-blur-md">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `font-sans-display text-sm uppercase tracking-widest-x ${
                                    isActive ? "text-aplum-accent" : "text-aplum-dim"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>
            </div>
        </header>
    );
};

export default Nav;
